import { Controller } from "@hotwired/stimulus"
import { Tooltip } from "bootstrap"
import { scheduleInitializeTruncatedElements } from "../utils/truncate"
import { scrollHorizontallyBy } from "../utils/index_scroll_table_scroll"

// Toggles edge state so the sticky actions border only shows while content
// is still hidden underneath the column.
//
// Porte aussi les boutons ‹ › logés dans l'en-tête de la colonne actions — le seul trou de la
// ligne d'en-têtes, et il est collé à droite donc toujours visible. Ils rejouent exactement le
// pas des flèches clavier (cf. index_scroll_table_scroll) et s'éteignent en butée, ce qui dit
// d'un coup d'œil qu'il reste — ou non — des colonnes de ce côté. Rien n'apparaît tant que le
// tableau tient dans sa largeur.
//
// Sur téléphone, chaque ligne s'affiche en carte (_index_scroll_table.scss), chaque valeur
// précédée du libellé de sa colonne : le contrôleur le pose sur les cellules (`data-label`), repris
// des en-têtes du tableau, aussi pour les lignes ajoutées ensuite. Rien à décrire par modèle.
export default class extends Controller {
  static targets = ["scrollNav", "scrollButton"]

  connect() {
    this.update = this.update.bind(this)
    this.resizeObserver = new ResizeObserver(this.update)
    this.resizeObserver.observe(this.element)
    const table = this.element.querySelector("table")
    if (table) this.resizeObserver.observe(table)
    this.element.addEventListener("scroll", this.update, { passive: true })
    this.update()
    this.initializeTooltips()
    scheduleInitializeTruncatedElements(this.element)

    this.labelCells()
    const body = this.element.querySelector(":scope > table > tbody")
    if (body) {
      this.rowsObserver = new MutationObserver(() => this.labelCells())
      this.rowsObserver.observe(body, { childList: true })
    }
  }

  disconnect() {
    this.element.removeEventListener("scroll", this.update)
    this.resizeObserver?.disconnect()
    this.rowsObserver?.disconnect()
  }

  // `:scope > table` : pas les tableaux imbriqués (lignes dépliables), qui ont leur contrôleur.
  labelCells() {
    const labels = new Map()
    this.element.querySelectorAll(":scope > table > thead th[data-column]").forEach((header) => {
      labels.set(header.dataset.column, headerLabel(header))
    })

    this.element.querySelectorAll(":scope > table > tbody > tr > td[data-column]").forEach((cell) => {
      const label = labels.get(cell.dataset.column)
      if (label && cell.dataset.label !== label) cell.dataset.label = label
    })
  }

  initializeTooltips() {
    this.element.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((element) => {
      Tooltip.getOrCreateInstance(element)
    })
  }

  scroll(event) {
    const direction = Number(event.currentTarget.dataset.scrollDirection)
    if (!direction) return

    scrollHorizontallyBy(this.element, direction)
  }

  update() {
    const { scrollLeft, clientWidth, scrollWidth } = this.element
    const overflowing = scrollWidth > clientWidth + 1
    const atStart = scrollLeft <= 1
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1

    this.element.classList.toggle("index-scroll-table--overflowing", overflowing)
    this.element.classList.toggle("index-scroll-table--at-end", !overflowing || atEnd)
    this.updateScrollButtons(overflowing, atStart, atEnd)
    scheduleInitializeTruncatedElements(this.element)
  }

  // Un index peut en contenir un autre (lignes dépliables) : on passe par les cibles Stimulus,
  // qui s'arrêtent au contrôleur le plus proche, plutôt que par une classe sur le conteneur —
  // sinon un tableau extérieur qui déborde afficherait aussi les flèches du tableau imbriqué.
  updateScrollButtons(overflowing, atStart, atEnd) {
    this.scrollNavTargets.forEach((nav) => {
      nav.classList.toggle("index-scroll-table__scroll-nav--visible", overflowing)
    })

    this.scrollButtonTargets.forEach((button) => {
      const direction = Number(button.dataset.scrollDirection)
      button.disabled = !overflowing || (direction < 0 ? atStart : atEnd)
    })
  }
}

// Ni le menu d'un filtre, ni son bouton (le compteur d'un filtre actif), ni ses sous-filtres
// (sous-statut, sous-produit… : « Statut », pas « Statut · Raison de la résiliation · … »), ni
// les textes réservés aux lecteurs d'écran.
const LABEL_SKIPPED =
  ".dropdown-menu, .index-header-filter__toggle, .index-header-filter__summary, .index-header-filter-nested, .visually-hidden, template"

// Texte affiché de l'en-tête, ses blocs séparés : « Cabinet Démo · Destinataire », société et rôle
// étant deux lignes de l'en-tête du pointage, que `textContent` collait. Les variantes masquées sur
// téléphone (`d-none d-md-inline`) sont laissées de côté.
function headerLabel(header) {
  const parts = []
  let text = ""
  const flush = () => {
    const part = text.replace(/\s+/g, " ").trim()
    if (part) parts.push(part)
    text = ""
  }
  const walk = (node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        text += child.textContent
        return
      }
      if (child.nodeType !== Node.ELEMENT_NODE || child.matches(LABEL_SKIPPED)) return

      const { display } = window.getComputedStyle(child)
      if (display === "none") return

      const block = !display.startsWith("inline") && display !== "contents"
      if (block) flush()
      walk(child)
      if (block) flush()
    })
  }

  walk(header)
  flush()
  return parts.join(" · ")
}
